import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { fetchJsonCached } from '../../lib/settingsCache';
import ActivityListPage from './ActivityListPage';
import TourCategoryLandingPage from './TourCategoryLandingPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Root-level tours & charters page: /aqua-marine-dive-center,
 * /booking-private-powerboat-charters-in-st-john-vi, ...
 *
 * The legacy site published both excursion companies AND its booking/category pages at
 * the root of the domain, so those exact URLs are kept to preserve their search ranking.
 * A single root slug can therefore be:
 *   - a charter company        -> that company's listings
 *   - a category landing page  -> the porthole tiles (category has a landing_path)
 *   - a category listing page  -> the activity cards for that category
 * Because this route sits at the root it stays strict: anything that matches nothing
 * falls through to the home page like any other unknown URL.
 */
const CompanySlugPage = () => {
  const { slug } = useParams();
  const [kind, setKind] = useState('checking');

  useEffect(() => {
    let cancelled = false;
    setKind('checking');

    const verify = async () => {
      try {
        const [sellers, categories] = await Promise.all([
          fetchJsonCached(`${API_URL}/api/public/tours-charters/sellers`),
          fetchJsonCached(`${API_URL}/api/public/tours-charters/categories`),
        ]);
        if (cancelled) return;
        const sellerMatch = (Array.isArray(sellers) ? sellers : []).some(
          (c) => c?.slug === slug || c?.public_slug === slug || (c?.legacy_slugs || []).includes(slug),
        );
        if (sellerMatch) {
          setKind('company');
          return;
        }
        const category = (Array.isArray(categories) ? categories : []).find(
          (c) => c?.slug === slug || c?.legacy_path === slug || c?.landing_path === slug,
        );
        if (category) {
          setKind(category.landing_path === slug ? 'landing' : 'category');
          return;
        }
        setKind('missing');
      } catch (error) {
        if (!cancelled) setKind('missing');
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (kind === 'checking') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="company-slug-loading">
        <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (kind === 'missing') return <Navigate to="/" replace />;
  if (kind === 'landing') return <TourCategoryLandingPage />;
  if (kind === 'category') return <ActivityListPage mode="category" />;
  return <ActivityListPage mode="company" />;
};

export default CompanySlugPage;
